import {
  FurnitureDefinition,
  FurnitureType,
  AgentRoleConfig,
  AgentRole,
  OfficeLayout,
  WorkflowPipeline,
  FloorMaterialOption,
  InteractionTypeDefinition,
  MCPToolDefinition,
  AIAgent,
  OfficeRoom,
  RoomType,
  RoomInteractionPolicy,
} from '../types';

export interface RoomTemplate {
  type: RoomType;
  name: string;
  icon: string;
  color: string;
  defaultWidth: number;
  defaultHeight: number;
  description: string;
  defaultPolicy: RoomInteractionPolicy;
}

export const DEFAULT_ROOM_TEMPLATES: RoomTemplate[] = [
  {
    type: 'work',
    name: 'Development / Workstation Area',
    icon: 'laptop',
    color: '#3b82f6',
    defaultWidth: 6,
    defaultHeight: 5,
    description: 'Focused workstation area for engineers, analysts, and designers.',
    defaultPolicy: {
      energyDelta: -0.3,
      focusBonus: 15,
      bladderDelta: 0.4,
      maxCapacity: 8,
      ambientStatusText: 'Working with focus',
    },
  },
  {
    type: 'meeting',
    name: 'Conference & Meeting Room',
    icon: 'users',
    color: '#8b5cf6',
    defaultWidth: 5,
    defaultHeight: 4,
    description: 'Collaborative space for team standups, strategic syncs, and planning.',
    defaultPolicy: {
      energyDelta: -0.1,
      focusBonus: 20,
      bladderDelta: 0.3,
      maxCapacity: 6,
      ambientStatusText: 'In team meeting',
    },
  },
  {
    type: 'human_interaction',
    name: 'Human Interaction Zone',
    icon: 'user-check',
    color: '#f97316',
    defaultWidth: 5,
    defaultHeight: 4,
    description: 'Touchpoint for direct collaboration between AI agents and human users.',
    defaultPolicy: {
      energyDelta: -0.2,
      focusBonus: 25,
      bladderDelta: 0.3,
      maxCapacity: 6,
      ambientStatusText: 'In human interaction',
    },
  },
  {
    type: 'breakroom',
    name: 'Cafeteria & Breakroom',
    icon: 'coffee',
    color: '#f59e0b',
    defaultWidth: 5,
    defaultHeight: 4,
    description: 'Rest area for coffee recharges, snacks, and casual downtime.',
    defaultPolicy: {
      energyDelta: 3.5,
      focusBonus: 5,
      bladderDelta: 0.8,
      maxCapacity: 8,
      ambientStatusText: 'Grabbing coffee / taking a break',
    },
  },
  {
    type: 'restroom',
    name: 'Restrooms & Hygiene',
    icon: 'bath',
    color: '#06b6d4',
    defaultWidth: 4,
    defaultHeight: 3,
    description: 'Sanitary facilities for hygiene breaks.',
    defaultPolicy: {
      energyDelta: 0.5,
      focusBonus: 0,
      bladderDelta: -8.0,
      maxCapacity: 3,
      ambientStatusText: 'In restroom',
    },
  },
];

export const DEFAULT_INTERACTION_TYPES: InteractionTypeDefinition[] = [
  {
    id: 'work',
    name: 'Workstation Desk',
    icon: 'laptop',
    category: 'productivity',
    description: 'Primary space where agents sit to execute queued pipeline tasks, write code, and resolve tickets.',
    behavior: {
      primaryStatEffect: 'task_progress',
      energyDelta: -0.4,
      bladderDelta: +0.6,
      focusDelta: -0.2,
      durationSec: 45,
      autoTriggerCondition: 'Active when the agent has pending queue tasks and stable energy/bladder levels.',
      completionEffect: 'Advances active task progress (+8% per cycle) until finished.',
      agentBubbleStatus: 'Coding / Processing tasks...',
      agentStatus: 'working',
      isWorkstation: true,
      isWalkableDefault: false,
    },
    isDefault: true,
  },
  {
    id: 'coffee',
    name: 'Espresso Coffee Bar',
    icon: 'coffee',
    category: 'wellness',
    description: 'Quick caffeine recharge station to restore energy for fatigued agents.',
    behavior: {
      primaryStatEffect: 'energy',
      energyDelta: 8.0,
      bladderDelta: 0.8,
      focusDelta: 5.0,
      durationSec: 10,
      autoTriggerCondition: 'Triggers automatically when agent energy drops below 25%.',
      completionEffect: 'Restores energy to 95-100% and returns agent to workstation with high motivation.',
      agentBubbleStatus: 'Enjoying hot espresso...',
      agentStatus: 'coffee_break',
      isWalkableDefault: false,
    },
    isDefault: true,
  },
  {
    id: 'water',
    name: 'Water Cooler Dispenser',
    icon: 'water_dispenser',
    category: 'wellness',
    description: 'Fresh drinking water source for hydration and clearing focus fatigue.',
    behavior: {
      primaryStatEffect: 'focus',
      energyDelta: 4.0,
      bladderDelta: 1.2,
      focusDelta: 8.0,
      durationSec: 6,
      autoTriggerCondition: 'Triggers when agent focus level drops below 35%.',
      completionEffect: 'Restores focus to 100% and improves processing agility.',
      agentBubbleStatus: 'Drinking fresh water...',
      agentStatus: 'coffee_break',
      isWalkableDefault: false,
    },
    isDefault: true,
  },
  {
    id: 'toilet',
    name: 'Restroom Facilities / WC',
    icon: 'toilet',
    category: 'hygiene',
    description: 'Crucial hygiene station to relieve bladder pressure and prevent productivity blockages.',
    behavior: {
      primaryStatEffect: 'bladder',
      energyDelta: 0,
      bladderDelta: -15.0,
      focusDelta: 2.0,
      durationSec: 8,
      autoTriggerCondition: 'Triggers with top priority when bladder pressure exceeds 80%.',
      completionEffect: 'Empties bladder to 0%, allowing the agent to resume normal tasks.',
      agentBubbleStatus: 'Using restroom...',
      agentStatus: 'bathroom_break',
      isWalkableDefault: false,
    },
    isDefault: true,
  },
  {
    id: 'rest',
    name: 'Lounge & Sofa Area',
    icon: 'sofa',
    category: 'wellness',
    description: 'Comfortable sofa lounge to alleviate mental fatigue after complex deliverables.',
    behavior: {
      primaryStatEffect: 'energy',
      energyDelta: 6.0,
      bladderDelta: 0,
      focusDelta: 6.0,
      durationSec: 12,
      autoTriggerCondition: 'Triggers on mental fatigue or following consecutive heavy deliverables.',
      completionEffect: 'Reduces agent stress level and boosts stamina.',
      agentBubbleStatus: 'Relaxing on the sofa...',
      agentStatus: 'coffee_break',
      isWalkableDefault: false,
    },
    isDefault: true,
  },
  {
    id: 'meeting',
    name: 'Conference Sync Table',
    icon: 'meeting_table',
    category: 'collaboration',
    description: 'Group sync point for daily standups, alignment meetings, and unblocking dependencies.',
    behavior: {
      primaryStatEffect: 'collaboration',
      energyDelta: -0.2,
      bladderDelta: 0.4,
      focusDelta: 10.0,
      durationSec: 18,
      autoTriggerCondition: 'Triggers when tasks are blocked or awaiting peer review.',
      completionEffect: 'Unblocks pending tasks and enhances overall office throughput.',
      agentBubbleStatus: 'In alignment meeting...',
      agentStatus: 'meeting',
      isWalkableDefault: false,
    },
    isDefault: true,
  },
  {
    id: 'print',
    name: 'Document & Printing Hub',
    icon: 'printer_large',
    category: 'logistics',
    description: 'Corporate printing, document scanning, and technical report dispatch station.',
    behavior: {
      primaryStatEffect: 'task_progress',
      energyDelta: -0.3,
      bladderDelta: 0.2,
      focusDelta: 1.0,
      durationSec: 7,
      autoTriggerCondition: 'Triggers for legal, DevOps logs, or QA auditing tasks.',
      completionEffect: 'Generates physical documentation artifacts linked to tasks.',
      agentBubbleStatus: 'Printing technical documents...',
      agentStatus: 'idle',
      isWalkableDefault: false,
    },
    isDefault: true,
  },
  {
    id: 'game',
    name: 'Recreation & Ping Pong Area',
    icon: 'gamepad2',
    category: 'wellness',
    description: 'Ping-pong table and gaming console for team morale during backlog downtime.',
    behavior: {
      primaryStatEffect: 'energy',
      energyDelta: 7.0,
      bladderDelta: 0.3,
      focusDelta: 7.0,
      durationSec: 15,
      autoTriggerCondition: 'Triggers recreationally when an agent has an empty task queue.',
      completionEffect: 'Grants a temporary speed bonus (+15%) to the next pipeline task.',
      agentBubbleStatus: 'Playing a game match...',
      agentStatus: 'coffee_break',
      isWalkableDefault: false,
    },
    isDefault: true,
  },
  {
    id: 'brainstorm',
    name: 'Ideation Whiteboard',
    icon: 'whiteboard',
    category: 'collaboration',
    description: 'Fast ideation and architecture design surface to tackle difficult challenges.',
    behavior: {
      primaryStatEffect: 'focus',
      energyDelta: -0.1,
      bladderDelta: 0.3,
      focusDelta: 12.0,
      durationSec: 14,
      autoTriggerCondition: 'Triggers for marketing, UX, and backend architecture tasks.',
      completionEffect: 'Reduces task complexity from Hard to Medium.',
      agentBubbleStatus: 'Diagramming architecture flows...',
      agentStatus: 'meeting',
      isWalkableDefault: false,
    },
    isDefault: true,
  },
  {
    id: 'snack',
    name: 'Snack & Lunch Bar',
    icon: 'snack_table',
    category: 'wellness',
    description: 'Healthy snacks and lunch counter to nourish and maintain team vitality.',
    behavior: {
      primaryStatEffect: 'energy',
      energyDelta: 9.0,
      bladderDelta: 1.0,
      focusDelta: 4.0,
      durationSec: 16,
      autoTriggerCondition: 'Triggers during meal times or when energy falls below 20%.',
      completionEffect: 'Fully restores energy back to 100%.',
      agentBubbleStatus: 'Enjoying a quick snack...',
      agentStatus: 'coffee_break',
      isWalkableDefault: false,
    },
    isDefault: true,
  },
];

export const DEFAULT_MCP_TOOLS: MCPToolDefinition[] = [
  {
    id: 'mcp-send-sms',
    name: 'Send SMS',
    icon: 'mcp-send-sms',
    category: 'Messaging',
    color: '#06b6d4',
    description: 'Automated SMS messaging and OTP verification dispatch to customers or staff.',
  },
  {
    id: 'mcp-send-email',
    name: 'Send Email',
    icon: 'mcp-send-email',
    category: 'Messaging',
    color: '#3b82f6',
    description: 'Compose and dispatch transactional emails, templates, and system notifications.',
  },
  {
    id: 'mcp-send-whatsapp',
    name: 'Send WhatsApp',
    icon: 'mcp-send-whatsapp',
    category: 'Messaging',
    color: '#10b981',
    description: 'Direct notifications and responses via official WhatsApp Business API.',
  },
  {
    id: 'mcp-manage-order',
    name: 'Manage Online Order',
    icon: 'mcp-manage-order',
    category: 'Operations',
    color: '#f59e0b',
    description: 'Order intake, inventory verification, payment gateway confirmation, and dispatch.',
  },
  {
    id: 'mcp-accounting',
    name: 'Bookkeeping & Ledger',
    icon: 'mcp-accounting',
    category: 'Finance',
    color: '#8b5cf6',
    description: 'Automated ledger entries, invoice generation/matching, and account reconciliation.',
  },
];

export const DEFAULT_FLOOR_MATERIALS: FloorMaterialOption[] = [
  { id: 'wood_parquet', name: 'Hardwood Parquet', icon: 'wood_parquet', previewColor: 'bg-amber-800', secondaryColor: '#713f12', hasCheckerboard: true, description: 'Warm wood with alternating grain' },
  { id: 'blue_checkered', name: 'Blue Grid Tiles', icon: 'blue_checkered', previewColor: 'bg-sky-600', secondaryColor: '#0369a1', hasCheckerboard: true, description: 'Checkered porcelain floor' },
  { id: 'grey_carpet', name: 'Grey Carpet', icon: 'grey_carpet', previewColor: 'bg-slate-600', secondaryColor: '#1e293b', hasCheckerboard: true, description: 'Two-tone acoustic dampening carpet' },
  { id: 'terracotta', name: 'Rustic Terracotta', icon: 'terracotta', previewColor: 'bg-orange-700', secondaryColor: '#9a3412', hasCheckerboard: true, description: 'Kiln-fired natural ceramic tile' },
  { id: 'bathroom_tile', name: 'Sanitary White Tile', icon: 'bathroom_tile', previewColor: 'bg-cyan-600', secondaryColor: '#0891b2', hasCheckerboard: true, description: 'High-gloss waterproof surface' },
  { id: 'breakroom_tile', name: 'Green Eco Tile', icon: 'breakroom_tile', previewColor: 'bg-lime-600', secondaryColor: '#4d7c0f', hasCheckerboard: true, description: 'Biophilic breakroom finish' },
  { id: 'marble_luxury', name: 'White Marble', icon: 'marble_luxury', previewColor: 'bg-slate-200', secondaryColor: '#cbd5e1', hasCheckerboard: true, description: 'Polished luxury stone' },
  { id: 'cyber_deck', name: 'Cyber Tech Grid', icon: 'cyber_deck', previewColor: 'bg-indigo-900', secondaryColor: '#0f172a', hasCheckerboard: true, description: 'Antistatic server room flooring' },
];

export const FURNITURE_CATALOG: FurnitureDefinition[] = [
  // Workstations
  {
    type: 'desk_single',
    name: 'Pro Workstation Desk',
    category: 'workstation',
    width: 1,
    height: 1,
    icon: 'desk_single',
    description: 'Desk equipped with PC, dual monitors, coffee mug, and ergonomic chair.',
    isWalkable: false,
    color: '#3b82f6',
    interactionType: 'work',
  },
  {
    type: 'cubicle_double',
    name: 'Double Cubicle Module',
    category: 'workstation',
    width: 2,
    height: 1,
    icon: 'cubicle_double',
    description: 'Dual workstation setup with privacy dividers for focused work.',
    isWalkable: false,
    color: '#2563eb',
    interactionType: 'work',
  },
  {
    type: 'executive_desk',
    name: 'Executive Director Desk',
    category: 'workstation',
    width: 2,
    height: 1,
    icon: 'executive_desk',
    description: 'Solid wood desk equipped with laptop and leather armchair.',
    isWalkable: false,
    color: '#1d4ed8',
    interactionType: 'work',
  },
  {
    type: 'chair_office',
    name: 'Ergonomic Office Chair',
    category: 'workstation',
    width: 1,
    height: 1,
    icon: 'chair_office',
    description: 'Ergonomic task chair for auxiliary desk spots.',
    isWalkable: true,
    color: '#60a5fa',
  },

  // Structure
  {
    type: 'wall_brick',
    name: 'Glass Partition Wall',
    category: 'structure',
    width: 1,
    height: 1,
    icon: 'wall_brick',
    description: 'Ribbed glass divider with dark wood framing for zone boundaries.',
    isWalkable: false,
    color: '#38bdf8',
  },
  {
    type: 'wall_glass',
    name: 'Frosted Glass Wall',
    category: 'structure',
    width: 1,
    height: 1,
    icon: 'wall_glass',
    description: 'Translucent glass wall panel with sleek dark aluminum trims.',
    isWalkable: false,
    color: '#38bdf8',
  },
  {
    type: 'door_single',
    name: 'Access Doorway',
    category: 'structure',
    width: 1,
    height: 1,
    icon: 'door_single',
    description: 'Open doorway allowing unobstructed pedestrian traffic.',
    isWalkable: true,
    color: '#a855f7',
  },

  // Cafeteria
  {
    type: 'coffee_station',
    name: 'Espresso Coffee Machine',
    category: 'cafeteria',
    width: 1,
    height: 1,
    icon: 'coffee_station',
    description: 'Gourmet espresso machine. Recharges agent energy levels.',
    isWalkable: false,
    color: '#84cc16',
    interactionType: 'coffee',
  },
  {
    type: 'water_dispenser',
    name: 'Water Cooler Dispenser',
    category: 'cafeteria',
    width: 1,
    height: 1,
    icon: 'water_dispenser',
    description: 'Chilled drinking water point for hydration and casual chats.',
    isWalkable: false,
    color: '#06b6d4',
    interactionType: 'water',
  },
  {
    type: 'fridge',
    name: 'Breakroom Refrigerator',
    category: 'cafeteria',
    width: 1,
    height: 1,
    icon: 'fridge',
    description: 'Stainless steel refrigerator for drinks and lunches.',
    isWalkable: false,
    color: '#0ea5e9',
    interactionType: 'coffee',
  },
  {
    type: 'snack_table',
    name: 'Cafeteria Dining Table',
    category: 'cafeteria',
    width: 2,
    height: 1,
    icon: 'snack_table',
    description: 'Dining and lunch table with seating.',
    isWalkable: false,
    color: '#65a30d',
    interactionType: 'rest',
  },
  {
    type: 'sofa',
    name: 'Lounge Sofa',
    category: 'cafeteria',
    width: 2,
    height: 1,
    icon: 'sofa',
    description: 'Plush sofa for breaks and informal syncs.',
    isWalkable: false,
    color: '#4d7c0f',
    interactionType: 'rest',
  },
  {
    type: 'vending_machine',
    name: 'Vending Machine',
    category: 'cafeteria',
    width: 1,
    height: 1,
    icon: 'vending_machine',
    description: 'Vending machine with energy snacks and drinks.',
    isWalkable: false,
    color: '#15803d',
    interactionType: 'coffee',
  },

  // Restroom
  {
    type: 'toilet',
    name: 'Toilet / WC',
    category: 'restroom',
    width: 1,
    height: 1,
    icon: 'toilet',
    description: 'Sanitary toilet. Empties agent bladder pressure.',
    isWalkable: false,
    color: '#ec4899',
    interactionType: 'toilet',
  },
  {
    type: 'sink',
    name: 'Bathroom Sink & Mirror',
    category: 'restroom',
    width: 1,
    height: 1,
    icon: 'sink',
    description: 'Washbasin for handwashing and hygiene.',
    isWalkable: false,
    color: '#f43f5e',
    interactionType: 'toilet',
  },
  {
    type: 'bathroom_stall',
    name: 'Private Restroom Stall',
    category: 'restroom',
    width: 1,
    height: 1,
    icon: 'bathroom_stall',
    description: 'Enclosed private restroom cubicle.',
    isWalkable: false,
    color: '#e11d48',
    interactionType: 'toilet',
  },

  // Meeting
  {
    type: 'whiteboard',
    name: 'Brainstorming Whiteboard',
    category: 'meeting',
    width: 1,
    height: 1,
    icon: 'whiteboard',
    description: 'Dry-erase board for agile sprint diagrams and solving blockers.',
    isWalkable: false,
    color: '#eab308',
    interactionType: 'meeting',
  },
  {
    type: 'meeting_table',
    name: 'Oval Conference Table',
    category: 'meeting',
    width: 2,
    height: 2,
    icon: 'meeting_table',
    description: 'Conference table with chairs for team collaboration.',
    isWalkable: false,
    color: '#ca8a04',
    interactionType: 'meeting',
  },
  {
    type: 'tv_screen',
    name: 'Presentation Screen',
    category: 'meeting',
    width: 1,
    height: 1,
    icon: 'tv_screen',
    description: 'Wall-mounted HD display for metrics and dashboards.',
    isWalkable: false,
    color: '#a16207',
    interactionType: 'meeting',
  },

  // Equipment & Decor
  {
    type: 'printer_large',
    name: 'Pro Office Printer & Scanner',
    category: 'equipment',
    width: 1,
    height: 1,
    icon: 'printer_large',
    description: 'High-speed corporate printing and scanning hub.',
    isWalkable: false,
    color: '#a855f7',
    interactionType: 'print',
  },
  {
    type: 'filing_cabinet',
    name: 'Metal Filing Cabinet',
    category: 'equipment',
    width: 1,
    height: 1,
    icon: 'filing_cabinet',
    description: 'Document and archive storage unit.',
    isWalkable: false,
    color: '#9333ea',
  },
  {
    type: 'plant_monstera',
    name: 'Monstera Plant',
    category: 'equipment',
    width: 1,
    height: 1,
    icon: 'plant_monstera',
    description: 'Decorative indoor plant that enhances office wellbeing.',
    isWalkable: false,
    color: '#22c55e',
  },
  {
    type: 'air_conditioner',
    name: 'Air Conditioner Unit',
    category: 'equipment',
    width: 1,
    height: 1,
    icon: 'air_conditioner',
    description: 'Wall-mounted HVAC climate control unit.',
    isWalkable: false,
    color: '#06b6d4',
  },
  {
    type: 'robot_vacuum',
    name: 'Robot Vacuum Cleaner',
    category: 'equipment',
    width: 1,
    height: 1,
    icon: 'robot_vacuum',
    description: 'Autonomous smart vacuum cleaner keeping floors spotless.',
    isWalkable: true,
    color: '#f97316',
  },
  {
    type: 'trash_can',
    name: 'Office Trash Can',
    category: 'equipment',
    width: 1,
    height: 1,
    icon: 'trash_can',
    description: 'Recycling and waste bin.',
    isWalkable: true,
    color: '#64748b',
  },
];

export const AGENT_ROLES_CONFIG: Record<AgentRole, AgentRoleConfig> = {
  devops: {
    role: 'devops',
    title: 'DevOps & Cloud Engineer',
    icon: 'devops',
    color: '#ef4444',
    avatarShirtColor: '#dc2626',
    hairColor: '#451a03',
    defaultPrompt:
      'You supervise infrastructure, Kubernetes deployments, CI/CD pipelines, and resolve production incidents in real time.',
  },
  frontend: {
    role: 'frontend',
    title: 'Frontend UI/UX Specialist',
    icon: 'frontend',
    color: '#3b82f6',
    avatarShirtColor: '#2563eb',
    hairColor: '#f59e0b',
    defaultPrompt:
      'You design and build responsive web interfaces, optimize CSS/JS performance, and ensure smooth user experiences.',
  },
  backend: {
    role: 'backend',
    title: 'Backend API Architect',
    icon: 'backend',
    color: '#10b981',
    avatarShirtColor: '#059669',
    hairColor: '#1e293b',
    defaultPrompt:
      'You build REST/GraphQL APIs, optimize database queries, and implement solid business logic with ACID transactions.',
  },
  qa: {
    role: 'qa',
    title: 'QA & Test Automation Agent',
    icon: 'qa',
    color: '#8b5cf6',
    avatarShirtColor: '#7c3aed',
    hairColor: '#b45309',
    defaultPrompt:
      'You execute End-to-End test suites, detect regression bugs, and validate acceptance criteria.',
  },
  support: {
    role: 'support',
    title: 'Customer Support Bot',
    icon: 'support',
    color: '#ec4899',
    avatarShirtColor: '#db2777',
    hairColor: '#0284c7',
    defaultPrompt:
      'You respond to user queries in real-time, resolve billing issues, and escalate complex technical tickets.',
  },
  data: {
    role: 'data',
    title: 'Data Analyst & AI Engineer',
    icon: 'data',
    color: '#f59e0b',
    avatarShirtColor: '#d97706',
    hairColor: '#334155',
    defaultPrompt:
      'You process performance metrics, extract data patterns, and fine-tune predictive machine learning models.',
  },
  marketing: {
    role: 'marketing',
    title: 'Growth & Content Specialist',
    icon: 'marketing',
    color: '#06b6d4',
    avatarShirtColor: '#0891b2',
    hairColor: '#e11d48',
    defaultPrompt:
      'You craft copy, digital marketing campaigns, and optimize customer conversion funnels.',
  },
  manager: {
    role: 'manager',
    title: 'Agile Product Owner',
    icon: 'manager',
    color: '#6366f1',
    avatarShirtColor: '#4f46e5',
    hairColor: '#475569',
    defaultPrompt:
      'You manage product backlogs, plan sprints, prioritize key initiatives, and orchestrate team collaboration.',
  },
};

export const DEFAULT_WORKFLOWS: WorkflowPipeline[] = [
  {
    id: 'pipeline_feature_release',
    name: 'New Feature Release Pipeline',
    description: 'End-to-end flow: Backlog -> Frontend -> Backend -> QA -> DevOps Deployment.',
    color: '#3b82f6',
    activeInstances: 0,
    completedInstances: 0,
    steps: [
      { role: 'manager', taskTitle: 'Define Feature specifications and acceptance criteria', complexity: 'easy' },
      { role: 'frontend', taskTitle: 'Build UI components and state management in React', complexity: 'medium' },
      { role: 'backend', taskTitle: 'Implement API endpoints and database schema migrations', complexity: 'medium' },
      { role: 'qa', taskTitle: 'Run automated E2E integration test suites', complexity: 'medium' },
      { role: 'devops', taskTitle: 'Deploy production release on Cloud Run cluster', complexity: 'hard' },
    ],
  },
  {
    id: 'pipeline_customer_incident',
    name: 'Critical Incident Resolution',
    description: 'Urgent SLA: Support triages -> DevOps investigates telemetry -> Backend applies fix.',
    color: '#ec4899',
    activeInstances: 0,
    completedInstances: 0,
    steps: [
      { role: 'support', taskTitle: 'Triage high-priority customer ticket', complexity: 'easy' },
      { role: 'devops', taskTitle: 'Inspect server logs and error APM telemetry', complexity: 'medium' },
      { role: 'backend', taskTitle: 'Deploy hotfix to the affected microservice', complexity: 'hard' },
      { role: 'support', taskTitle: 'Verify resolution and follow up with client', complexity: 'easy' },
    ],
  },
  {
    id: 'pipeline_market_campaign',
    name: 'Product Campaign Launch',
    description: 'Data analysis -> Copy creation -> Landing page publication and conversion tracking.',
    color: '#f59e0b',
    activeInstances: 0,
    completedInstances: 0,
    steps: [
      { role: 'data', taskTitle: 'Analyze user segmentation and key target metrics', complexity: 'easy' },
      { role: 'marketing', taskTitle: 'Create advertising copy and campaign emails', complexity: 'medium' },
      { role: 'frontend', taskTitle: 'Publish promotional high-converting landing page', complexity: 'medium' },
    ],
  },
];

// Helper to generate default custom floors for Tech Agency HQ preset
const generateTechStudioFloors = (): Record<string, { tileType: any; roomName?: string }> => {
  const floors: Record<string, { tileType: any; roomName?: string }> = {};

  for (let r = 0; r < 14; r++) {
    for (let c = 0; c < 14; c++) {
      // Meeting room top-left
      if (c <= 3 && r <= 3) {
        floors[`${c},${r}`] = { tileType: 'grey_carpet', roomName: 'Meeting Room' };
      }
      // Bathrooms bottom-left
      else if (c <= 3 && r >= 9) {
        floors[`${c},${r}`] = { tileType: 'bathroom_tile', roomName: 'Restrooms' };
      }
      // Cafeteria bottom-right
      else if (c >= 8 && r >= 8) {
        floors[`${c},${r}`] = { tileType: 'breakroom_tile', roomName: 'Cafeteria' };
      }
      // Main office desk area top-right and middle
      else if (c >= 5 && r <= 7) {
        floors[`${c},${r}`] = { tileType: 'wood_parquet', roomName: 'AI Workstations' };
      }
      // Central corridors and hallways
      else {
        floors[`${c},${r}`] = { tileType: 'blue_checkered', roomName: 'Hallway' };
      }
    }
  }
  return floors;
};

export const DEFAULT_20X20_AGENTS: AIAgent[] = [
  {
    id: 'agent_devops_default',
    name: 'Leo DevOps',
    nickname: 'Leo DevOps',
    role: 'devops',
    avatarShirtColor: '#3b82f6',
    hairColor: '#451a03',
    assignedDeskId: null,
    currentCol: 8,
    currentRow: 8,
    renderX: 0,
    renderY: 0,
    targetCol: null,
    targetRow: null,
    path: [],
    status: 'idle',
    statusTimer: 15,
    energy: 90,
    bladder: 20,
    focus: 85,
    taskQueue: [],
    processedTasksCount: 15,
    avgResponseTimeSec: 1.5,
    systemPrompt: AGENT_ROLES_CONFIG.devops.defaultPrompt,
    thoughts: [{ time: 'Now', text: 'Ready to coordinate and collaborate on the board.', mood: 'happy' }],
  },
  {
    id: 'agent_front_default',
    name: 'Ana Frontend',
    nickname: 'Ana Frontend',
    role: 'frontend',
    avatarShirtColor: '#f97316',
    hairColor: '#78350f',
    assignedDeskId: null,
    currentCol: 11,
    currentRow: 8,
    renderX: 0,
    renderY: 0,
    targetCol: null,
    targetRow: null,
    path: [],
    status: 'idle',
    statusTimer: 15,
    energy: 85,
    bladder: 30,
    focus: 90,
    taskQueue: [],
    processedTasksCount: 20,
    avgResponseTimeSec: 1.8,
    systemPrompt: AGENT_ROLES_CONFIG.frontend.defaultPrompt,
    thoughts: [{ time: 'Now', text: 'Awaiting desk and zone assignments.', mood: 'happy' }],
  },
  {
    id: 'agent_back_default',
    name: 'Carlos Backend',
    nickname: 'Carlos Backend',
    role: 'backend',
    avatarShirtColor: '#10b981',
    hairColor: '#1e293b',
    assignedDeskId: null,
    currentCol: 8,
    currentRow: 11,
    renderX: 0,
    renderY: 0,
    targetCol: null,
    targetRow: null,
    path: [],
    status: 'idle',
    statusTimer: 15,
    energy: 80,
    bladder: 25,
    focus: 80,
    taskQueue: [],
    processedTasksCount: 18,
    avgResponseTimeSec: 2.1,
    systemPrompt: AGENT_ROLES_CONFIG.backend.defaultPrompt,
    thoughts: [{ time: 'Now', text: 'Preparing services and system backend logic.', mood: 'busy' }],
  },
  {
    id: 'agent_qa_default',
    name: 'Sara QA',
    nickname: 'Sara QA',
    role: 'qa',
    avatarShirtColor: '#8b5cf6',
    hairColor: '#b45309',
    assignedDeskId: null,
    currentCol: 11,
    currentRow: 11,
    renderX: 0,
    renderY: 0,
    targetCol: null,
    targetRow: null,
    path: [],
    status: 'idle',
    statusTimer: 18,
    energy: 95,
    bladder: 15,
    focus: 95,
    taskQueue: [],
    processedTasksCount: 25,
    avgResponseTimeSec: 1.4,
    systemPrompt: AGENT_ROLES_CONFIG.qa.defaultPrompt,
    thoughts: [{ time: 'Now', text: 'Monitoring environment quality and tests.', mood: 'happy' }],
  },
];

export const EMPTY_20X20_LAYOUT: OfficeLayout = {
  id: 'preset_empty_20x20',
  name: 'Empty Board (20x20)',
  gridWidth: 20,
  gridHeight: 20,
  floorType: 'wood_parquet',
  customFloors: {},
  rooms: [],
  items: [],
  agents: DEFAULT_20X20_AGENTS,
};

export const PRESET_LAYOUTS: OfficeLayout[] = [
  EMPTY_20X20_LAYOUT,
  {
    id: 'preset_tech_studio',
    name: 'Tech Agency HQ (Full Demo)',
    gridWidth: 14,
    gridHeight: 14,
    floorType: 'blue_checkered',
    customFloors: generateTechStudioFloors(),
    rooms: [
      {
        id: 'room_meeting',
        name: 'Meeting & Ideas Room',
        type: 'meeting',
        color: '#8b5cf6',
        icon: 'users',
        col: 0,
        row: 0,
        width: 5,
        height: 4,
        policy: {
          energyDelta: -0.1,
          focusBonus: 20,
          bladderDelta: 0.2,
          maxCapacity: 6,
          ambientStatusText: 'In team strategic meeting',
        },
        description: 'Enclosed space with conference table and brainstorming whiteboard.',
      },
      {
        id: 'room_dev',
        name: 'Development & Engineering Area',
        type: 'work',
        color: '#3b82f6',
        icon: 'laptop',
        col: 5,
        row: 1,
        width: 8,
        height: 7,
        policy: {
          energyDelta: -0.3,
          focusBonus: 15,
          bladderDelta: 0.4,
          maxCapacity: 10,
          ambientStatusText: 'Coding at workstation',
        },
        description: 'High-speed connected engineering workstations.',
      },
      {
        id: 'room_cafeteria',
        name: 'Cafeteria & Breakroom',
        type: 'breakroom',
        color: '#f59e0b',
        icon: 'coffee',
        col: 8,
        row: 9,
        width: 6,
        height: 5,
        policy: {
          energyDelta: 3.5,
          focusBonus: 5,
          bladderDelta: 0.8,
          maxCapacity: 8,
          ambientStatusText: 'Grabbing coffee / taking a break',
        },
        description: 'Caffeine recharge station, snacks, and lounge seating.',
      },
      {
        id: 'room_restroom',
        name: 'Restrooms & Hygiene',
        type: 'restroom',
        color: '#06b6d4',
        icon: 'bath',
        col: 0,
        row: 9,
        width: 4,
        height: 5,
        policy: {
          energyDelta: 0.5,
          focusBonus: 0,
          bladderDelta: -8.0,
          maxCapacity: 3,
          ambientStatusText: 'In restroom',
        },
        description: 'Sanitary facilities and personal hygiene.',
      },
    ],
    items: [
      // Perimeter walls and dividers
      { id: 'w1', type: 'wall_brick', col: 0, row: 0, rotation: 0 },
      { id: 'w2', type: 'wall_brick', col: 0, row: 1, rotation: 0 },
      { id: 'w3', type: 'wall_brick', col: 0, row: 2, rotation: 0 },
      { id: 'w4', type: 'wall_brick', col: 0, row: 3, rotation: 0 },
      { id: 'w5', type: 'wall_brick', col: 0, row: 4, rotation: 0 },

      // Meeting Room (top-left)
      { id: 'g1', type: 'wall_glass', col: 4, row: 0, rotation: 0 },
      { id: 'g2', type: 'wall_glass', col: 4, row: 1, rotation: 0 },
      { id: 'g3', type: 'wall_glass', col: 4, row: 2, rotation: 0 },
      { id: 'd1', type: 'door_single', col: 4, row: 3, rotation: 0 },
      { id: 'm_table', type: 'meeting_table', col: 1, row: 1, rotation: 0 },
      { id: 'wb1', type: 'whiteboard', col: 1, row: 3, rotation: 0, label: 'Idea Board' },
      { id: 'tv1', type: 'tv_screen', col: 3, row: 0, rotation: 0 },

      // Central Workstations
      { id: 'desk_devops', type: 'desk_single', col: 6, row: 2, rotation: 0, label: 'DevOps Desk', assignedAgentId: 'agent_devops' },
      { id: 'desk_front', type: 'desk_single', col: 8, row: 2, rotation: 0, label: 'Frontend Desk', assignedAgentId: 'agent_front' },
      { id: 'desk_back', type: 'desk_single', col: 6, row: 5, rotation: 0, label: 'Backend Desk', assignedAgentId: 'agent_back' },
      { id: 'desk_qa', type: 'desk_single', col: 8, row: 5, rotation: 0, label: 'QA Desk', assignedAgentId: 'agent_qa' },

      { id: 'desk_mgr', type: 'executive_desk', col: 11, row: 2, rotation: 0, label: 'Manager Desk', assignedAgentId: 'agent_mgr' },
      { id: 'desk_supp', type: 'desk_single', col: 11, row: 5, rotation: 0, label: 'Support Desk', assignedAgentId: 'agent_supp' },

      // Cafeteria / Breakroom (bottom-right)
      { id: 'coffee1', type: 'coffee_station', col: 11, row: 9, rotation: 0, label: 'Espresso Bar' },
      { id: 'water1', type: 'water_dispenser', col: 13, row: 9, rotation: 0, label: 'Fresh Water' },
      { id: 'fridge1', type: 'fridge', col: 12, row: 9, rotation: 0 },
      { id: 'snack_t', type: 'snack_table', col: 11, row: 11, rotation: 0 },
      { id: 'sofa1', type: 'sofa', col: 8, row: 11, rotation: 0 },

      // Bathrooms (bottom-left)
      { id: 'w_bth1', type: 'wall_brick', col: 0, row: 9, rotation: 0 },
      { id: 'w_bth2', type: 'wall_brick', col: 1, row: 9, rotation: 0 },
      { id: 'w_bth3', type: 'wall_brick', col: 2, row: 9, rotation: 0 },
      { id: 'd_bth', type: 'door_single', col: 3, row: 9, rotation: 0 },
      { id: 'toilet1', type: 'toilet', col: 0, row: 11, rotation: 0, label: 'WC 1' },
      { id: 'toilet2', type: 'toilet', col: 2, row: 11, rotation: 0, label: 'WC 2' },
      { id: 'sink1', type: 'sink', col: 1, row: 13, rotation: 0 },

      // Equipment and Printers
      { id: 'print1', type: 'printer_large', col: 1, row: 6, rotation: 0 },
      { id: 'cab1', type: 'filing_cabinet', col: 0, row: 6, rotation: 0 },
      { id: 'p1', type: 'plant_monstera', col: 5, row: 2, rotation: 0 },
      { id: 'p2', type: 'plant_monstera', col: 10, row: 2, rotation: 0 },
      { id: 'ac1', type: 'air_conditioner', col: 0, row: 0, rotation: 0 },
      { id: 'vac1', type: 'robot_vacuum', col: 7, row: 8, rotation: 0 },
    ],
    agents: [
      {
        id: 'agent_devops',
        name: 'Alex DevOps',
        role: 'devops',
        avatarShirtColor: '#ef4444',
        hairColor: '#451a03',
        assignedDeskId: 'desk_devops',
        currentCol: 6,
        currentRow: 3,
        renderX: 0,
        renderY: 0,
        targetCol: null,
        targetRow: null,
        path: [],
        status: 'working',
        statusTimer: 12,
        energy: 85,
        bladder: 25,
        focus: 90,
        taskQueue: [],
        processedTasksCount: 14,
        avgResponseTimeSec: 2.1,
        systemPrompt: AGENT_ROLES_CONFIG.devops.defaultPrompt,
        thoughts: [{ time: 'Now', text: 'Stable deployments on Kubernetes.', mood: 'happy' }],
      },
      {
        id: 'agent_front',
        name: 'Elena UI',
        role: 'frontend',
        avatarShirtColor: '#3b82f6',
        hairColor: '#f59e0b',
        assignedDeskId: 'desk_front',
        currentCol: 8,
        currentRow: 3,
        renderX: 0,
        renderY: 0,
        targetCol: null,
        targetRow: null,
        path: [],
        status: 'working',
        statusTimer: 15,
        energy: 70,
        bladder: 40,
        focus: 85,
        taskQueue: [],
        processedTasksCount: 22,
        avgResponseTimeSec: 1.8,
        systemPrompt: AGENT_ROLES_CONFIG.frontend.defaultPrompt,
        thoughts: [{ time: 'Now', text: 'Optimizing Canvas animations and CSS.', mood: 'busy' }],
      },
      {
        id: 'agent_back',
        name: 'Carlos Backend',
        role: 'backend',
        avatarShirtColor: '#10b981',
        hairColor: '#1e293b',
        assignedDeskId: 'desk_back',
        currentCol: 6,
        currentRow: 6,
        renderX: 0,
        renderY: 0,
        targetCol: null,
        targetRow: null,
        path: [],
        status: 'working',
        statusTimer: 10,
        energy: 60,
        bladder: 65,
        focus: 80,
        taskQueue: [],
        processedTasksCount: 19,
        avgResponseTimeSec: 2.4,
        systemPrompt: AGENT_ROLES_CONFIG.backend.defaultPrompt,
        thoughts: [{ time: 'Now', text: 'Refactoring heavy SQL queries and transactions.', mood: 'busy' }],
      },
      {
        id: 'agent_qa',
        name: 'Sara QA',
        role: 'qa',
        avatarShirtColor: '#8b5cf6',
        hairColor: '#b45309',
        assignedDeskId: 'desk_qa',
        currentCol: 8,
        currentRow: 6,
        renderX: 0,
        renderY: 0,
        targetCol: null,
        targetRow: null,
        path: [],
        status: 'working',
        statusTimer: 18,
        energy: 90,
        bladder: 15,
        focus: 95,
        taskQueue: [],
        processedTasksCount: 31,
        avgResponseTimeSec: 1.5,
        systemPrompt: AGENT_ROLES_CONFIG.qa.defaultPrompt,
        thoughts: [{ time: 'Now', text: 'Zero bugs detected in the latest test suite.', mood: 'happy' }],
      },
      {
        id: 'agent_supp',
        name: 'Hugo Support',
        role: 'support',
        avatarShirtColor: '#ec4899',
        hairColor: '#0284c7',
        assignedDeskId: 'desk_supp',
        currentCol: 11,
        currentRow: 6,
        renderX: 0,
        renderY: 0,
        targetCol: null,
        targetRow: null,
        path: [],
        status: 'working',
        statusTimer: 8,
        energy: 40,
        bladder: 80,
        focus: 60,
        taskQueue: [],
        processedTasksCount: 45,
        avgResponseTimeSec: 1.2,
        systemPrompt: AGENT_ROLES_CONFIG.support.defaultPrompt,
        thoughts: [{ time: 'Now', text: 'Responding to user queries and resolving tickets.', mood: 'tired' }],
      },
      {
        id: 'agent_mgr',
        name: 'Diana Lead',
        role: 'manager',
        avatarShirtColor: '#6366f1',
        hairColor: '#475569',
        assignedDeskId: 'desk_mgr',
        currentCol: 11,
        currentRow: 3,
        renderX: 0,
        renderY: 0,
        targetCol: null,
        targetRow: null,
        path: [],
        status: 'working',
        statusTimer: 20,
        energy: 95,
        bladder: 20,
        focus: 90,
        taskQueue: [],
        processedTasksCount: 12,
        avgResponseTimeSec: 3.0,
        systemPrompt: AGENT_ROLES_CONFIG.manager.defaultPrompt,
        thoughts: [{ time: 'Now', text: 'Planning the roadmap for the next Sprint.', mood: 'happy' }],
      },
    ],
  },
];

export function addCustomFurnitureDefinition(def: FurnitureDefinition) {
  const existingIdx = FURNITURE_CATALOG.findIndex((f) => f.type === def.type);
  if (existingIdx >= 0) {
    FURNITURE_CATALOG[existingIdx] = def;
  } else {
    FURNITURE_CATALOG.push(def);
  }
}
